/** @jsx jsx */
import * as React from 'react';
import { css, jsx } from '@emotion/core';
import { partial } from 'lodash';
import { TextInput } from '@zapier/design-system';
import { HostedField } from '@zapier/payment/src/components/CreditCardForm/HostedFields';
import CountrySelect from '@zapier/payment/src/components/CountrySelect';
import PaymentFormField from '@zapier/payment/src/components/PaymentFormField';
import PostalCodeInput from '@zapier/payment/src/components/PostalCodeInput';
import StateSelect from '@zapier/payment/src/components/StateSelect';
import {
  getValue,
  handleChange,
  hasError,
} from '@zapier/payment/src/utils/formHelpers';
import { useSize } from '@zapier/payment/src/hooks/useSize';
import { FieldName, Fields, SensitiveValue } from '@zapier/payment/src/types';

type Props = {
  /** Fields to show */
  fields: Fields;
  /** Call when field changes value */
  onChange: (name: FieldName, value: string | SensitiveValue) => void;
};

const rootStyle = css`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, 1fr);
`;

function CreditCardFieldset(props: Props) {
  const { fields, onChange } = props;
  const size = useSize();
  const isSmall = size === 'small';
  const id = React.useRef(Math.random() * 100);

  const getLabelId = (name: string): string => `${name}-label-${id.current}`;
  const getInputId = (name: string): string => `${name}-input-${id.current}`;

  const showAdditionalFields = getValue('addressCountry', fields) === 'US';

  return (
    <div css={rootStyle}>
      <PaymentFormField id={getLabelId('name')} label="Name" span={4}>
        <TextInput
          autoComplete="shipping name"
          id={getInputId('name')}
          isErrored={hasError('name', fields)}
          name="name"
          onChange={handleChange(onChange, 'name')}
          placeholder="Full name"
          value={getValue('name', fields)}
        />
      </PaymentFormField>
      <PaymentFormField label="Number" span={isSmall ? 4 : 2}>
        <HostedField
          hasError={hasError('number', fields)}
          onChange={partial(onChange, 'number')}
          type="number"
        />
      </PaymentFormField>
      <PaymentFormField label="Expiry" span={isSmall ? 2 : 1}>
        <HostedField
          hasError={hasError('expiry', fields)}
          onChange={partial(onChange, 'expiry')}
          type="expiry"
        />
      </PaymentFormField>
      <PaymentFormField label="CVC" span={isSmall ? 2 : 1}>
        <HostedField
          hasError={hasError('cvc', fields)}
          onChange={partial(onChange, 'cvc')}
          type="cvc"
        />
      </PaymentFormField>
      <PaymentFormField
        id={getLabelId('country')}
        label="Country"
        span={isSmall ? 4 : 2}
      >
        <CountrySelect
          hasError={hasError('addressCountry', fields)}
          labelId={getLabelId('country')}
          onChange={handleChange(onChange, 'addressCountry')}
          value={getValue('addressCountry', fields)}
        />
      </PaymentFormField>
      <PaymentFormField
        id={getLabelId('state')}
        isHidden={!showAdditionalFields}
        label="State"
        span={isSmall ? 4 : 1}
      >
        <StateSelect
          hasError={hasError('addressState', fields)}
          labelId={getLabelId('state')}
          onChange={handleChange(onChange, 'addressState')}
          value={getValue('addressState', fields)}
        />
      </PaymentFormField>
      <PaymentFormField
        id={getLabelId('addressZip')}
        isHidden={!showAdditionalFields}
        label="Zip Code"
        span={isSmall ? 2 : 1}
      >
        <PostalCodeInput
          hasError={hasError('addressZip', fields)}
          id={getInputId('addressZip')}
          labelId={getLabelId('addressZip')}
          onChange={handleChange(onChange, 'addressZip')}
          value={getValue('addressZip', fields)}
        />
      </PaymentFormField>
    </div>
  );
}

export default CreditCardFieldset;
